import { buildConnectorParametersDecryptUrl } from '../../utils/connectorParametersApi';

// Token from localStorage (same as your Autodesk code)
const token = localStorage.getItem('token');

/**
 * Google Drive OAuth bootstrap:
 * - mcName must be "GoogleDrive"
 * - Expects your backend to expose: synctrack/googledrive/fullTokens?mcId={mcId}
 * - Expects you to save the returned ?code from redirect into localStorage.AuthCode (same pattern as your Autodesk flow)
 */
export const getGoogleDriveCredentialsAndCreateToken = async (connectorId, mcId, masterConnectors) => {
  try {
    // 1) Find Google Drive master connector
    const googleConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === mcId?.toString() ||
       mc.mcId?.toString() === mcId?.toString()) &&
      mc.mcName === 'GoogleDrive'
    );
    if (!googleConnector) {
      throw new Error('GoogleDrive master connector not found');
    }

    // 2) Fetch encrypted params using connector instance ID (ctd_id)
    const paramsRes = await fetch(buildConnectorParametersDecryptUrl(connectorId), {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!paramsRes.ok) throw new Error('Failed to fetch connector parameters');
    const connectorParams = await paramsRes.json();

    const clientId = connectorParams.find(p => p.scpName === 'clientid')?.scpValue;
    if (!clientId) {
      throw new Error('Client ID not found in connector parameters');
    }

    // 4) Store connector id to complete post-redirect update
    localStorage.setItem('PendingGoogleDriveConnectorId', connectorId);

    // 5) Build Google OAuth URL
    // Use a separate redirect env for clarity; register it in Google Cloud Console (OAuth consent + credentials)
    const redirectUri = process.env.REACT_APP_GOOGLE_OAUTH_REDIRECT_URI || process.env.REACT_APP_OAUTH_REDIRECT_URI;

    // Scopes: adjust as needed (read-only vs full)
    const scopes = [
      'https://www.googleapis.com/auth/drive', // full Drive
      // or 'https://www.googleapis.com/auth/drive.readonly'
    ].join(' ');

    // Use Master Connector auth URL
    const authEndpoint = googleConnector.mcAuthurl;
    if (!authEndpoint) {
      throw new Error('Google Drive Authentication URL is missing from Master Connector data');
    }

    const googleAuthUrl =
      `${authEndpoint}${authEndpoint.includes('?') ? '&' : '?'}` +
      `response_type=code&` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scopes)}&` +
      `state=${encodeURIComponent(connectorId)}&` +
      `access_type=offline&` +  // request refresh_token
      `prompt=consent`;         // force prompt to ensure refresh_token is issued

    // 6) If code already stored (after redirect), exchange it; otherwise redirect
    const storedAuthCode = localStorage.getItem('AuthCode');
    if (storedAuthCode) {
      const tokenResponse = await exchangeGoogleDriveAuthCodeForToken(
        storedAuthCode,
        clientId,
        mcId,
        redirectUri
      );

      // Clean up
      localStorage.removeItem('AuthCode');
      localStorage.removeItem('PendingGoogleDriveConnectorId');

      return tokenResponse;
    } else {
      window.location.href = googleAuthUrl;
      return null; // will redirect
    }
  } catch (error) {
    console.error('Error getting Google Drive credentials:', error);
    throw error;
  }
};

// Helper: exchange code for tokens via your backend and update connector
export const exchangeGoogleDriveAuthCodeForToken = async (authCode, clientId, mcId, redirectUri) => {
  try {
    // Your backend should call Google's token endpoint (https://oauth2.googleapis.com/token)
    // and return the JSON (access_token, refresh_token, expires_in, token_type, scope)
    const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/google/token?mcId=${mcId}`;

    const response = await fetch(tokenUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/x-www-form-urlencoded',
        Accept: 'application/json',
      },
      body: new URLSearchParams({
        code: authCode,
        grant_type: 'authorization_code',
        redirect_uri: redirectUri,
        // Backend will use mcId to fetch/decrypt client credentials:
        client_id: clientId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Google token exchange error response:', errorText);
      throw new Error(`Failed to exchange authorization code for token: ${response.status}`);
    }

    const tokenData = await response.json();
    // tokenData should look like:
    // {
    //   access_token: "...",
    //   expires_in: 3599,
    //   refresh_token: "...",   // present on first consent with access_type=offline
    //   scope: "https://www.googleapis.com/auth/drive",
    //   token_type: "Bearer"
    // }

    // Update connector
    const connectorId = localStorage.getItem('PendingGoogleDriveConnectorId');
    if (connectorId) {
      const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;

      // Fetch current connector
      const connectorRes = await fetch(`${API_BASE_URL}/${connectorId}`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!connectorRes.ok) throw new Error(`Failed to fetch connector data: ${connectorRes.status}`);
      const connectorData = await connectorRes.json();

      // Parse expires_in - Google tokens typically expire in 3600 seconds (1 hour)
      const primaryExpiresIn = parseInt(tokenData.expires_in, 10) || 3600;
      
      // Ensure expires_in is valid (between 1 second and 24 hours)
      const validExpiresIn = Math.max(1, Math.min(primaryExpiresIn, 86400));
      
      if (primaryExpiresIn !== validExpiresIn) {
        console.warn(`Google token expires_in adjusted from ${primaryExpiresIn} to ${validExpiresIn} seconds`);
      }

      // Calculate token expiry - use ISO string format that backend can parse to LocalDateTime
      const tokenExpiryDate = new Date(Date.now() + validExpiresIn * 1000);
      const tokenExpiryISO = tokenExpiryDate.toISOString();
      
      console.log(`Setting token expiry: ${tokenExpiryISO} (${validExpiresIn} seconds from now)`);

      // Google refresh tokens are long-lived but can be revoked; set a conservative review window (e.g., 180 days)
      const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
      const refreshTokenExpiry = new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString();

      // Validate required fields
      if (!tokenData.access_token) {
        throw new Error('No access_token received from Google OAuth');
      }
      
      if (!tokenData.refresh_token && !connectorData.refreshToken) {
        console.warn('No refresh_token received from Google OAuth - token refresh may fail in the future');
      }

      const updatedConnectorData = {
        ...connectorData,
        token: tokenData.access_token,
        refreshToken: tokenData.refresh_token || connectorData.refreshToken || null,
        tokenExpiry: tokenExpiryISO, // ISO string format for backend to parse
        // Optional: keep a "secondary" slot unused for Google; null it explicitly
        secondaryToken: null,
        secondaryTokenExpiry: null,
        refreshUrl: connectorData.refreshUrl,
        refreshTokenExpiry,
        status: 'Connected',
        connectedTime: new Date().toISOString(),
        // Optionally store scope/provider metadata
        provider: 'GoogleDrive',
        scope: tokenData.scope,
      };
      
      console.log('Updating connector with token data:', {
        hasToken: !!updatedConnectorData.token,
        hasRefreshToken: !!updatedConnectorData.refreshToken,
        tokenExpiry: updatedConnectorData.tokenExpiry,
        status: updatedConnectorData.status
      });

      const updateRes = await fetch(`${API_BASE_URL}/${connectorId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedConnectorData),
      });

      if (!updateRes.ok) {
        const errorText = await updateRes.text();
        console.error('Update connector error:', errorText);
        throw new Error(`Failed to update connector with tokens: ${updateRes.status}`);
      }

      console.log('GoogleDrive connector updated successfully with tokens');
      // Optional: reload to reflect "Connected" status in UI
      window.location.reload();
    }

    return tokenData;
  } catch (error) {
    console.error('Google token exchange error:', error);
    throw error;
  }
};
